"use client";

import React, { useState, useMemo } from "react";
import { NativeBarChart } from "@/components/ui/NativeBarChart";
import {
  Shield,
  FileText,
  Users,
  Scale,
  AlertCircle,
  Lightbulb,
} from "lucide-react";

const formatCurrency = (value: number) => {
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(1).replace(/\.0$/, "")}B`;
  }
  if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (value >= 1e3) {
    return `$${(value / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

export function BurnRateEstimator() {
  const [sector, setSector] = useState<string>("SaaS");
  const [headcount, setHeadcount] = useState<number>(250);
  const [currentARR, setCurrentARR] = useState<number>(50000000);
  const [currentOpex, setCurrentOpex] = useState<number>(40000000);

  // Constants & Formulas
  // Institutional grade estimation based on typical De-SPAC year 1 costs

  const estimatedAuditFees = useMemo(() => {
    let base = 750000;
    if (sector === "Fintech") base += 250000; // More complex audits
    if (currentARR > 100000000) base += 500000;
    return base;
  }, [sector, currentARR]);

  const estimatedDAndO = useMemo(() => {
    let base = 1500000;
    if (sector === "Crypto") base += 1000000; // Crypto risk premium
    if (headcount > 500) base += 500000;
    return base;
  }, [sector, headcount]);

  const estimatedSoxCompliance = useMemo(() => {
    return 600000 + headcount * 1000; // Internal audit build out
  }, [headcount]);

  const estimatedSecLegal = useMemo(() => {
    return 800000; // General corporate counsel, 8K/10Q/10K filings, proxy
  }, []);

  const estimatedIRBoard = useMemo(() => {
    return 600000; // IR firm retainer + Independent director cash/equity
  }, []);

  const totalPublicToll =
    estimatedAuditFees +
    estimatedDAndO +
    estimatedSoxCompliance +
    estimatedSecLegal +
    estimatedIRBoard;
  const newPublicOpex = currentOpex + totalPublicToll;

  const burnRateIncreasePct = ((totalPublicToll / currentOpex) * 100).toFixed(
    1,
  );
  const insightText = `Going public will incrementally increase your annual operating expenses by ${formatCurrency(totalPublicToll)} (a ${burnRateIncreasePct}% increase to your baseline ${formatCurrency(currentOpex)} OPEX). This "public toll" represents the recurring fixed costs required to maintain SEC compliance, PCAOB audits, and institutional D&O insurance.`;

  const chartData = [
    {
      name: "Private OPEX",
      datasets: [{ label: "Baseline", value: currentOpex, color: "#94a3b8" }],
    },
    {
      name: "Public Toll",
      datasets: [
        { label: "Incremental Toll", value: totalPublicToll, color: "#f59e0b" },
      ],
    },
    {
      name: "Year 1 Public",
      datasets: [{ label: "Total", value: newPublicOpex, color: "#10b981" }],
    },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8">
      {/* Controls Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-6 bg-card rounded-2xl border border-border shadow-sm">
        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Sector Profile
          </label>
          <select
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none"
          >
            <option value="SaaS">Enterprise B2B SaaS / AI</option>
            <option value="Fintech">Financial Technology</option>
            <option value="Crypto">Crypto & Web3 Infra</option>
          </select>
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Current Headcount
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {headcount}
            </span>
          </label>
          <input
            type="range"
            min={50}
            max={2000}
            step={50}
            value={headcount}
            onChange={(e) => setHeadcount(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Current ARR
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {formatCurrency(currentARR)}
            </span>
          </label>
          <input
            type="range"
            min={20000000}
            max={500000000}
            step={10000000}
            value={currentARR}
            onChange={(e) => setCurrentARR(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Current Annual OPEX
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {formatCurrency(currentOpex)}
            </span>
          </label>
          <input
            type="range"
            min={10000000}
            max={500000000}
            step={5000000}
            value={currentOpex}
            onChange={(e) => setCurrentOpex(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>
      </div>

      {/* Analytics Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        {/* Cost Breakdown Cards */}
        <div className="space-y-4">
          <div className="p-6 bg-card rounded-2xl border border-border shadow-sm">
            <div className="flex justify-between items-end mb-6 pb-4 border-b border-border">
              <div>
                <h3 className="text-xl font-bold">The Public Toll</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Estimated incremental recurring public company costs.
                </p>
              </div>
              <div className="text-right">
                <span className="block text-sm text-muted-foreground uppercase font-bold tracking-wider">
                  Total
                </span>
                <span className="text-3xl font-mono text-orange-500 font-bold">
                  {formatCurrency(totalPublicToll)}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              {/* Line Items */}
              <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="p-2 bg-blue-500/10 text-blue-500 rounded-md shrink-0">
                  <Shield size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between font-semibold">
                    <span>D&O Insurance (Public Rate)</span>
                    <span className="font-mono">
                      {formatCurrency(estimatedDAndO)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Directors & Officers liability premiums surge at go-public,
                    heavily influenced by sector ({sector}).
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="p-2 bg-emerald-500/10 text-emerald-500 rounded-md shrink-0">
                  <FileText size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between font-semibold">
                    <span>PCAOB Audits & Tax</span>
                    <span className="font-mono">
                      {formatCurrency(estimatedAuditFees)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Shift from private statements to PCAOB-level 10-K/10-Q
                    continuous auditing.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="p-2 bg-purple-500/10 text-purple-500 rounded-md shrink-0">
                  <AlertCircle size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between font-semibold">
                    <span>SOX & Internal Controls</span>
                    <span className="font-mono">
                      {formatCurrency(estimatedSoxCompliance)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Building out Sarbanes-Oxley 404 compliance architecture and
                    internal audit staff.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="p-2 bg-orange-500/10 text-orange-500 rounded-md shrink-0">
                  <Scale size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between font-semibold">
                    <span>SEC Legal Counsel</span>
                    <span className="font-mono">
                      {formatCurrency(estimatedSecLegal)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Specialized securities counsel for filings and continuous
                    disclosure obligations.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="p-2 bg-foreground/10 text-foreground rounded-md shrink-0">
                  <Users size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between font-semibold">
                    <span>IR Agency & Board Comp</span>
                    <span className="font-mono">
                      {formatCurrency(estimatedIRBoard)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Investor relations management, press wire fees, and required
                    independent director retainers.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 bg-card rounded-2xl border border-border shadow-sm flex flex-col justify-center min-h-[400px]">
          <h3 className="text-xl font-bold mb-2 text-center">OPEX Expansion</h3>
          <p className="text-sm text-center text-muted-foreground mb-12">
            Visualizing the step-up in fixed operating costs.
          </p>
          <div className="w-full h-full pb-4 pr-4">
            <NativeBarChart data={chartData} formatCurrency={formatCurrency} />
          </div>
        </div>
      </div>

      {/* Dynamic Data Insight */}
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 flex gap-4 items-start shadow-sm mt-8">
        <div className="p-3 bg-primary/10 rounded-full text-primary mt-1 flex-shrink-0">
          <Lightbulb size={24} />
        </div>
        <div>
          <h4 className="font-bold text-foreground mb-1 text-lg">
            Key Takeaway
          </h4>
          <p className="text-muted-foreground text-lg text-balance leading-relaxed">
            {insightText}
          </p>
        </div>
      </div>
      {/* Disclaimer */}
      <div className="mt-12 text-xs text-muted-foreground/80 leading-relaxed max-w-4xl mx-auto text-center border-t border-border/50 pt-8">
        <p>
          <strong>Disclaimer:</strong> This tool is for informational and
          educational purposes only and does not constitute financial,
          investment, legal, or tax advice. The outputs generated are estimates
          based on specific assumptions and mathematical models. Actual
          transaction structures, dilution effects, and financial returns may
          vary significantly. Please consult with appropriate professional
          advisors before making any business decisions.
        </p>
      </div>
    </div>
  );
}
